import React, { useState, useEffect } from 'react';
import { Paper, Button, TextField, FormControl, InputLabel, Select, MenuItem, Alert, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utills/axiosInstance';

const LeaveRequest = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [balanceInfo, setBalanceInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');

  const { register, handleSubmit, setValue, watch } = useForm();
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');
  const leaveType = watch('leaveType');

  const navigate = useNavigate();
  const profile = JSON.parse(localStorage.getItem('profile') || '{}');
  const userId = profile._id;

  useEffect(() => {
    if (!userId) return;

    const loadData = async () => {
      try {
        const [typesRes, blockedRes] = await Promise.all([
          axiosInstance.get('/api/leave/types'),
          axiosInstance.get(`/api/blocked-dates/${userId}`)
        ]);

        setLeaveTypes(typesRes.data);
        setBlockedDates(blockedRes.data.blockedDates || []);
      } catch (err) {
        setError('Failed to load data');
      }
    };

    loadData();
  }, [userId]);

  useEffect(() => {
    if (!leaveType || !userId) {
      setBalanceInfo(null);
      return;
    }

    const fetchBalance = async () => {
      try {
        const res = await axiosInstance.get(`/api/leave/balance/${userId}`);
        const selected = res.data.find(item => item.leaveType._id === leaveType);
        setBalanceInfo(selected || { available: 0, leaveType: { name: 'Unknown' } });
      } catch (err) {
        console.error(err);
      }
    };

    fetchBalance();
  }, [leaveType, userId]);

  useEffect(() => {
    if (!fromDate) {
      setValue('duration', 0);
      return;
    }

    const start = new Date(fromDate);
    const end = toDate ? new Date(toDate) : start;
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (end < start) {
      setError('To date cannot be before From date');
      setValue('duration', 0);
      return;
    }

    let count = 0;
    const temp = new Date(start);

    while (temp <= end) {
      if (temp.getDay() !== 0) {
        count++;
      }
      temp.setDate(temp.getDate() + 1);
    }

    setValue('duration', count);
    setError('');
  }, [fromDate, toDate, setValue]);

  const shouldDisableDate = (date) => {
    if (!date) return true;
    if (date.getDay() === 0) {
      return true;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return blockedDates.includes(dateStr);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        leaveType: data.leaveType,
        fromDate: new Date(data.fromDate),
        toDate: data.toDate ? new Date(data.toDate) : new Date(data.fromDate),
        reason: data.reason || ''
      };

      await axiosInstance.post(`/api/leave/requests/${userId}`, payload);
      setError('Leave applied successfully!');
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to apply leave');
    } finally {
      setLoading(false);
    }
  };

  const duration = watch('duration') || 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Typography variant="h5" textAlign="center" mb={4}>
          Apply for Leave
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Leave Type *</InputLabel>
            <Select
              value={leaveType || ''}
              onChange={(e) => setValue('leaveType', e.target.value)}
              label="Leave Type"
              {...register('leaveType', { required: true })}
            >
              <MenuItem value=""><em>Select Type</em></MenuItem>
              {leaveTypes.map(t => (
                <MenuItem key={t._id} value={t._id}>
                  {t.name} — {t.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {balanceInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{balanceInfo.leaveType.name}</strong>: {balanceInfo.available} days available
              {duration > 0 && balanceInfo.available < duration && (
                <span style={{ color: 'red', marginLeft: 8 }}>
                  (Insufficient: Need {duration}, Have {balanceInfo.available})
                </span>
              )}
            </Alert>
          )}

          <DatePicker
            label="From Date *"
            value={fromDate}
            onChange={(date) => setValue('fromDate', date)}
            shouldDisableDate={shouldDisableDate}
            slotProps={{ textField: { fullWidth: true, sx: { mb: 3 } } }}
          />

          <DatePicker
            label="To Date (Optional for 1 day)"
            value={toDate}
            minDate={fromDate}
            onChange={(date) => setValue('toDate', date)}
            shouldDisableDate={shouldDisableDate}
            slotProps={{ textField: { fullWidth: true, sx: { mb: 3 } } }}
          />

          <TextField
            label="Duration (Days)"
            value={duration}
            disabled
            fullWidth
            sx={{ mb: 3 }}
          />

          <TextField
            {...register('reason')}
            label="Reason"
            multiline
            rows={3}
            required={true}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            sx={{ mb: 3 }}
          />

          {error && (
            <Alert severity={error.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !fromDate || !leaveType || !reason || (balanceInfo && balanceInfo.available < duration)}
          >
            {loading ? 'Applying...' : 'Apply Leave'}
          </Button>
        </form>
      </Paper>
    </LocalizationProvider>
  );
};

export default LeaveRequest;
